import { useMemo } from 'react';
import { collection } from 'firebase/firestore';
import { db } from '../../firebase';
import { useCollection, useCollectionOnce, useDoc } from './hooks';
import { parseProgram } from './program';
import { programRef } from './services';

/** Live exercise program for an id. Returns { program, loading, exists }. */
export function useProgram(programId) {
  const { data, loading, exists } = useDoc(programId ? programRef(programId) : null);
  const program = useMemo(() => (data ? parseProgram(data.program) : null), [data]);
  return { program, loading, exists };
}

/** `live: false` reads once (another user's data, e.g. trainer → athlete). */
export function useProgressDays(uid, { live = true } = {}) {
  const key = uid ? `pw/${uid}` : null;
  const read = live ? useCollection : useCollectionOnce;
  const { docs, loading, error } = read(uid ? collection(db, 'progress_workouts', uid, 'days') : null, key);
  return { progressDays: docs, loading, error };
}

/** `live: false` reads once (another user's data, e.g. trainer → athlete). */
export function useNutritionDays(uid, { live = true } = {}) {
  const key = uid ? `nd/${uid}` : null;
  const read = live ? useCollection : useCollectionOnce;
  const { docs, loading, error } = read(uid ? collection(db, 'users', uid, 'nutrition_days') : null, key);
  const sorted = useMemo(() => [...docs].sort((a, b) => String(b.date || b.id).localeCompare(String(a.date || a.id))), [docs]);
  return { nutritionDays: sorted, loading, error };
}

/** `live: false` reads once (another user's data, e.g. trainer → athlete). */
export function useBodyDays(uid, { live = true } = {}) {
  const key = uid ? `pb/${uid}` : null;
  const read = live ? useCollection : useCollectionOnce;
  const { docs, loading, error } = read(uid ? collection(db, 'progress_body', uid, 'days') : null, key);
  const sorted = useMemo(
    () => docs.filter((d) => d.weight_kg != null || d.body_fat_percent != null).sort((a, b) => a.id.localeCompare(b.id)),
    [docs],
  );
  return { bodyDays: sorted, loading, error };
}

/** `live: false` reads once (another user's data, e.g. trainer → athlete). */
export function useWaterDays(uid, { live = true } = {}) {
  const key = uid ? `wd/${uid}` : null;
  const read = live ? useCollection : useCollectionOnce;
  const { docs, loading } = read(uid ? collection(db, 'users', uid, 'water_days') : null, key);
  const byDate = useMemo(() => Object.fromEntries(docs.map((d) => [d.id, Math.max(0, Number(d.ml) || 0)])), [docs]);
  return { waterByDate: byDate, loading };
}

export function useEarnedBadges(uid) {
  const key = uid ? `bd/${uid}` : null;
  const { docs, loading } = useCollection(uid ? collection(db, 'users', uid, 'badges') : null, key);
  // `earned`: id → latest earn date (a repeatable badge's last repeat); `counts`: id → times earned.
  const earned = useMemo(
    () => Object.fromEntries(docs.map((d) => [d.id, (d.lastEarnedAt || d.earnedAt)?.toDate?.() || null])),
    [docs],
  );
  const counts = useMemo(() => Object.fromEntries(docs.map((d) => [d.id, Math.max(1, Number(d.count) || 1)])), [docs]);
  return { earned, counts, loading };
}
